<script lang="ts">
    import {getContext} from 'svelte';
    import {keyDevice} from '$lib/context-keys';
    import type {Device} from '$lib/stores/ui/device';

    interface Props {
        maxScroll: number
        onScroll: (newScroll: number) => void
    }

    let {
        maxScroll,
        onScroll,
    }: Props = $props()

    const device: Device = getContext(keyDevice)
    const hideButtonDelay = $derived($device === 'desktop' ? 1400 : 700)

    let previousScroll = 0
    let scrollDirection: 'up' | 'down' = $state('down')

    let scrollingTimeout: ReturnType<typeof setTimeout> | undefined = undefined
    let scrolling = $state(false)

    let lastTimestamp: number | null = $state(null) // start with null onMount so that first call of setCurrentScroll can be handled explicitly and won't have a really short difference --> trigger scrolling

    export function setCurrentScroll(newScroll: number) {
        // scrollDirection
        if (newScroll > previousScroll) {
            scrollDirection = 'down'
        } else if (newScroll < previousScroll) {
            scrollDirection = 'up'
        }
        previousScroll = newScroll

        // scrolling
        if (newScroll === 0 || newScroll === maxScroll) {
            scrolling = false
        } else {
            //navigating on mobile via tooltip did trigger afterScroll event (why?!)
            //>> prevent first and/or single event
            const currentTimestamp = Date.now()
            // first call
            if (!lastTimestamp) {
                lastTimestamp = currentTimestamp
                return
            }
            // subsequent call
            const lastChangeTooLongAgo = currentTimestamp - lastTimestamp > 100
            if (lastChangeTooLongAgo) {
                lastTimestamp = currentTimestamp
                return
            }
            lastTimestamp = currentTimestamp

            clearTimeout(scrollingTimeout)
            scrolling = true
            scrollingTimeout = setTimeout(() => scrolling = false, hideButtonDelay)
        }
    }

    function handleClick(event: Event) {
        event.stopPropagation()
        if (scrollDirection === 'up') {
            onScroll(0)
            setCurrentScroll(0)
        } else {
            onScroll(maxScroll)
            setCurrentScroll(maxScroll)
        }
    }

    // NOTE workaround with opacity and disablePointerEvents because with Svelte transition
    //  clicks on the button weren't triggered anymore on the out transition -> selecting rows below
    let disablePointerEvents = $state(true)

    function handleTransitionStart() {
        if (scrolling) disablePointerEvents = false
    }

    function handleTransitionEnd() {
        if (!scrolling) disablePointerEvents = true
    }
</script>

<div
        class:hide={!scrolling}
        style:pointer-events={disablePointerEvents ? 'none' : 'auto'}
        ontransitionstart={handleTransitionStart}
        ontransitionend={handleTransitionEnd}
>
    <button class="scroll-to-top-bottom-btn"
            aria-label="Scroll list to {scrollDirection === 'up' ? 'top' : 'bottom'}"
            data-testid="scroll-to-top-bottom-btn"
            onclick={handleClick}
    >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
             class:up={scrollDirection === 'up'}
             class:down={scrollDirection === 'down'}
        >
            <path fill="currentColor"
                  d="m19.164 12l-6.207-6.207l-1.414 1.414L16.336 12l-4.793 4.793l1.414 1.414zm-5.65 0L7.307 5.793L5.893 7.207L10.686 12l-4.793 4.793l1.414 1.414z"/>
        </svg>
    </button>
</div>

<style>
    div {
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 30%;
        padding: 1.4rem 0 2.4rem 0;
        display: grid;
        place-items: center;
        transition: opacity 600ms;
    }

    .scroll-to-top-bottom-btn {
        display: grid;
        padding: .5rem 1rem;
        background: var(--color-light);
        color: var(--color-main);
        border: 2px solid var(--color-dark);
        /*box-shadow: var(--shadow-toast);*/
    }

    .hide {
        opacity: 0;
    }

    .up {
        transform: rotate(-90deg);
    }

    .down {
        transform: rotate(90deg);
    }
</style>